import { CONFIG } from "./config.ts";
import { makeRng, randRange, dist2, TAU } from "./math.ts";
import { stageAt, xpToNext } from "./evolution.ts";
import { Player } from "./entities/Player.ts";
import { Poop } from "./entities/Poop.ts";
import { Being } from "./entities/Being.ts";
import { Buddha } from "./entities/Buddha.ts";
import { Camera } from "./systems/camera.ts";
import { Input } from "./systems/input.ts";
import { Audio } from "./systems/audio.ts";
import { Particles } from "./systems/particles.ts";
import { Poisons } from "./systems/poisons.ts";
import { Tilt } from "./systems/tilt.ts";
import { Mandala } from "./minigame/Mandala.ts";
import { Hud } from "./hud.ts";
import { drawCreature, drawPoop, drawBeing, drawBuddha, BEING_KINDS } from "./sprites.ts";
import type { BeingKind } from "./sprites.ts";

type Phase = "menu" | "playing" | "cleanse" | "dead" | "won";

const HISCORE_KEY = "poopingbuddha.hiscore";

export class Game {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private hud: Hud;
  private input: Input;
  private audio = new Audio();
  private particles = new Particles();
  private poisons = new Poisons();
  private tilt = new Tilt();
  private mandala: Mandala;
  private camera: Camera;

  private rng = makeRng(0x50f7);
  private player!: Player;
  private beings: Being[] = [];
  private buddha!: Buddha;
  private poops: Poop[] = [];

  private phase: Phase = "menu";
  private score = 0;
  private hiscore = 0;
  private goldenEaten = 0;
  private grace = 0; // birth grace: predators can't catch you yet
  private cleanseCount = 0; // successful cleanses this run → mandala difficulty

  private viewW = 0;
  private viewH = 0;
  private dpr = 1;
  private acc = 0;
  private last = 0;

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2D canvas not supported");
    this.ctx = ctx;

    this.camera = new Camera(CONFIG.world.width / 2, CONFIG.world.height / 2);
    this.input = new Input(canvas);
    this.mandala = new Mandala(uiRoot.parentElement ?? document.body, this.tilt);
    this.hud = new Hud(uiRoot);

    this.hiscore = Number(localStorage.getItem(HISCORE_KEY) ?? 0) || 0;

    this.hud.onStart = () => this.start();
    this.hud.onRestart = () => this.start();
    this.hud.onToggleMute = () => {
      this.audio.muted = !this.audio.muted;
      return this.audio.muted;
    };

    window.addEventListener("resize", () => this.resize());
    this.resize();
    this.reset();

    this.last = performance.now();
    requestAnimationFrame(this.frame);

    if (import.meta.env.DEV) {
      (window as unknown as { __game: unknown }).__game = {
        feed: (n: number) => {
          for (let i = 0; i < n; i++) this.poisons.eat(1, i % 2 ? "a" : "b", this.player.stage);
          const ev = this.player.addXp(n);
          for (let e = 0; e < ev; e++) this.onEvolve();
          this.updateHud();
        },
        fill: () => {
          this.poisons.defilement = CONFIG.fullness.max;
          this.enterCleanse();
        },
        state: () => ({
          phase: this.phase,
          stage: this.player.stage,
          name: this.player.stageName,
          score: Math.round(this.score),
          mindfulness: +this.poisons.mindfulness.toFixed(2),
          fullness: Math.round(this.poisons.fullnessFrac * 100),
          beings: this.beings.length,
          predators: this.beings.filter((b) => b.isPredatorTo(this.player.stage)).length,
        }),
      };
    }
  }

  private resize() {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.viewW = window.innerWidth;
    this.viewH = window.innerHeight;
    this.canvas.width = Math.floor(this.viewW * this.dpr);
    this.canvas.height = Math.floor(this.viewH * this.dpr);
    this.canvas.style.width = `${this.viewW}px`;
    this.canvas.style.height = `${this.viewH}px`;
  }

  // ---- lifecycle ---------------------------------------------------------

  private kindForTier(tier: number): BeingKind {
    return BEING_KINDS[Math.max(0, Math.min(BEING_KINDS.length - 1, tier - 1))];
  }

  /**
   * Spawn a being whose tier clusters around the player's realm: a spread of
   * kinds (so there's variety to eat) but all within the safe margin, so base
   * beings are food-droppers, not threats. Predators come from evolve waves.
   */
  private randomKind(): BeingKind {
    const stage = this.player?.stage ?? 0;
    const offsets = [-2, -1, -1, 0, 0, 0, 1, 1, 2]; // centered, weighted spread
    const off = offsets[Math.floor(this.rng() * offsets.length)];
    return this.kindForTier(stage + off);
  }

  private reset() {
    this.rng = makeRng((Math.floor(performance.now()) & 0xffff) ^ 0x50f7);
    this.player = new Player(CONFIG.world.width / 2, CONFIG.world.height / 2);
    this.beings = [];
    this.poops = [];
    this.poisons.reset();
    this.score = 0;
    this.goldenEaten = 0;
    this.cleanseCount = 0;

    for (let i = 0; i < CONFIG.beings.count; i++) this.beings.push(this.spawnBeing());
    this.buddha = new Buddha(
      randRange(this.rng, 200, CONFIG.world.width - 200),
      randRange(this.rng, 200, CONFIG.world.height - 200),
      this.rng,
    );

    // Every poop comes from someone: seed the world by having each being leave
    // a dropping or two near itself — no orphaned food.
    for (const b of this.beings) {
      const drops = 1 + Math.floor(this.rng() * 2);
      for (let k = 0; k < drops; k++) {
        const a = this.rng() * TAU;
        const d = randRange(this.rng, 10, 90);
        this.dropPoop(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d, b.tier, false, b.kind);
      }
    }

    this.camera.x = this.player.x;
    this.camera.y = this.player.y;
    this.updateHud();
  }

  private spawnBeing(kind?: BeingKind): Being {
    const k = kind ?? this.randomKind();
    // Keep spawns away from the player's start; keep predators even farther so
    // nobody is born on top of you.
    const cx = this.player.x;
    const cy = this.player.y;
    const minDist = 460;
    let x = 0;
    let y = 0;
    for (let tries = 0; tries < 20; tries++) {
      x = randRange(this.rng, 60, CONFIG.world.width - 60);
      y = randRange(this.rng, 60, CONFIG.world.height - 60);
      if (Math.hypot(x - cx, y - cy) >= minDist) break;
    }
    return new Being(x, y, k, this.rng);
  }

  /** Spawn a couple of fresh predators just off-screen to keep pressure on. */
  private spawnPredatorWave() {
    const tier = Math.min(7, this.player.stage + 3);
    for (let i = 0; i < 2; i++) {
      const a = this.rng() * TAU;
      const d = 700;
      const b = new Being(
        Math.max(60, Math.min(CONFIG.world.width - 60, this.player.x + Math.cos(a) * d)),
        Math.max(60, Math.min(CONFIG.world.height - 60, this.player.y + Math.sin(a) * d)),
        this.kindForTier(tier),
        this.rng,
      );
      this.beings.push(b);
    }
    // keep the population bounded
    while (this.beings.length > CONFIG.beings.count + 10) this.beings.shift();
  }

  private start() {
    this.audio.resume();
    void this.tilt.enable(); // request tilt permission now (we're in a tap)
    this.reset();
    this.grace = 3;
    this.phase = "playing";
  }

  private die() {
    if (this.phase !== "playing") return;
    this.phase = "dead";
    this.audio.death();
    this.particles.ring(this.player.x, this.player.y, "#c14a2c", 30, 260);
    this.camera.kick(14);
    this.score = Math.round(this.score);
    if (this.score > this.hiscore) {
      this.hiscore = this.score;
      localStorage.setItem(HISCORE_KEY, String(this.hiscore));
    }
    this.hud.showGameOver(this.player.stageName, this.score, this.hiscore);
  }

  private win() {
    this.phase = "won";
    this.audio.gong();
    this.particles.ring(this.player.x, this.player.y, "#ffd769", 40, 320);
    this.camera.kick(18);
    this.score = Math.round(this.score);
    if (this.score > this.hiscore) {
      this.hiscore = this.score;
      localStorage.setItem(HISCORE_KEY, String(this.hiscore));
    }
    this.hud.showWin(this.score, this.hiscore, this.goldenEaten);
  }

  // ---- spawning ----------------------------------------------------------

  private dropPoop(x: number, y: number, tier: number, golden: boolean, source: string) {
    if (this.poops.length >= CONFIG.poop.maxOnMap) {
      let oldest = -1;
      let oldestAge = -1;
      for (let i = 0; i < this.poops.length; i++) {
        if (!this.poops[i].golden && this.poops[i].age > oldestAge) {
          oldestAge = this.poops[i].age;
          oldest = i;
        }
      }
      if (oldest >= 0) this.poops.splice(oldest, 1);
    }
    this.poops.push(new Poop(x, y, tier, golden, this.rng() * TAU, source));
  }

  // ---- simulation --------------------------------------------------------

  private playerRef() {
    return { x: this.player.x, y: this.player.y, stage: this.player.stage, radius: this.player.radius };
  }

  private step(dt: number) {
    if (this.phase !== "playing") {
      this.player.animT += dt;
      for (const b of this.beings) b.animT += dt;
      this.buddha.animT += dt;
      this.particles.update(dt);
      return;
    }

    if (this.grace > 0) this.grace = Math.max(0, this.grace - dt);
    this.poisons.update(dt);
    // survival itself earns a trickle of karma
    this.score += dt * 2 * (1 + this.player.stage * 0.3);

    this.player.update(dt, this.input.dirX, this.input.dirY, this.poisons.speedFactor);

    const ref = this.playerRef();
    const allowHunt = this.grace <= 0;
    for (const b of this.beings) {
      if (b.update(dt, this.rng, ref, allowHunt)) {
        this.dropPoop(b.x, b.y, b.tier, false, b.kind);
        // visible "plop" so poop clearly comes from this being
        this.particles.burst(b.x, b.y + b.radius * 0.6, "#7a5836", 4, 60);
      }
    }
    if (this.buddha.update(dt, this.rng)) this.dropPoop(this.buddha.x, this.buddha.y, 6, true, "golden");

    for (let i = this.poops.length - 1; i >= 0; i--) {
      if (this.poops[i].update(dt)) this.poops.splice(i, 1);
    }

    this.handleEating();
    if (this.grace <= 0) this.checkPredators();

    this.camera.update(dt, this.player.x, this.player.y, this.player.stage);
    this.camera.clampToWorld(this.viewW, this.viewH);
    this.particles.update(dt);
    this.updateHud();
  }

  private handleEating() {
    const p = this.player;
    const reach = p.radius + CONFIG.player.eatReach;
    for (let i = this.poops.length - 1; i >= 0; i--) {
      const poop = this.poops[i];
      const rr = reach + poop.radius;
      if (dist2(p.x, p.y, poop.x, poop.y) <= rr * rr) {
        this.eat(poop);
        this.poops.splice(i, 1);
        if (this.phase !== "playing") break; // cleanse/death interrupted the loop
      }
    }
  }

  private eat(poop: Poop) {
    const p = this.player;

    if (poop.golden) {
      const gained = Math.ceil(xpToNext(p.stage) * 0.5);
      this.goldenEaten++;
      this.score += gained * 2;
      this.poisons.purify(0.4);
      this.poisons.grantClarity();
      this.audio.golden();
      this.particles.ring(poop.x, poop.y, "#ffd769", 22, 240);
      this.particles.float(poop.x, poop.y - poop.radius, `+${gained} ✨`, "#ffe08a", 26);
      this.hud.goldenFlash();
      this.camera.kick(6);
      const evolves = p.addXp(gained);
      for (let e = 0; e < evolves; e++) this.onEvolve();
      if (p.isEnlightened) this.win();
      return;
    }

    const res = this.poisons.eat(poop.tier, poop.source, p.stage);
    const gained = Math.max(0.2, res.value);
    this.score += gained;

    if (res.greedy) {
      this.audio.greedy();
      this.particles.float(poop.x, poop.y - poop.radius, "greedy", "#c98", 16);
    } else {
      this.audio.eat();
      const label = res.onBeat ? `+${gained.toFixed(0)} ☯` : `+${gained.toFixed(0)}`;
      this.particles.float(poop.x, poop.y - poop.radius, label, res.newType ? "#8fe3c2" : "#fff", 20);
    }
    this.particles.burst(poop.x, poop.y, "#a9713f", 6, 110);

    const evolves = p.addXp(gained);
    for (let e = 0; e < evolves; e++) this.onEvolve();

    if (p.isEnlightened) {
      this.win();
    } else if (res.full) {
      this.enterCleanse();
    }
  }

  private enterCleanse() {
    this.phase = "cleanse";
    // Difficulty rises with each successful cleanse this run (+ a little per realm).
    const level = this.cleanseCount + Math.floor(this.player.stage / 3);
    this.mandala.start(level, (result) => {
      if (result === "full") {
        // Only a completed trace cleanses.
        this.poisons.purify(1);
        this.poisons.grantClarity();
        this.cleanseCount++;
        this.audio.cleanse();
        this.hud.banner("Purified", "the mind clears");
        this.phase = "playing";
        this.updateHud();
      } else {
        // Ran out of incense — no cleanse. Still full, so try again.
        this.audio.greedy();
        this.hud.banner("Focus lost", "still defiled — try again");
        this.enterCleanse();
      }
    });
  }

  private checkPredators() {
    const p = this.player;
    for (const b of this.beings) {
      if (!b.isPredatorTo(p.stage)) continue;
      const caught = (p.radius + b.radius) * CONFIG.predators.eatOverlap;
      if (dist2(p.x, p.y, b.x, b.y) <= caught * caught) {
        this.die();
        return;
      }
    }
  }

  private onEvolve() {
    const p = this.player;
    const st = stageAt(p.stage);
    this.audio.evolve();
    this.hud.evolveFlash();
    this.hud.banner(`${st.name}`, st.blurb);
    this.particles.ring(p.x, p.y, st.color, 26, 240);
    this.camera.kick(10);
    p.squash = 1.35;
    this.score += 50 * p.stage; // ascension bonus
    if (!p.isEnlightened) this.spawnPredatorWave();
  }

  private updateHud() {
    const p = this.player;
    this.hud.setStage(p.stage);
    this.hud.setMeter(p.isEnlightened ? 1 : p.xp / p.xpNeeded);
    this.hud.setFullness(this.poisons.fullnessFrac, this.poisons.overfull);
    this.hud.setMindfulness(this.poisons.mindfulness);
    this.hud.setClarity(this.poisons.clarity > 0);
    this.hud.setBreath(this.poisons.breathScale);
    this.hud.setScore(Math.round(this.score));
  }

  // ---- rendering ---------------------------------------------------------

  private frame = (now: number) => {
    let dt = (now - this.last) / 1000;
    this.last = now;
    if (dt > 0.1) dt = 0.1;

    // The cleanse mini-game runs its own loop; freeze the world beneath it.
    if (this.phase !== "cleanse") {
      this.acc += dt;
      const step = CONFIG.sim.dt;
      let sub = 0;
      while (this.acc >= step && sub < CONFIG.sim.maxSubSteps) {
        this.step(step);
        this.acc -= step;
        sub++;
      }
      if (sub === CONFIG.sim.maxSubSteps) this.acc = 0;
    }

    this.render();
    requestAnimationFrame(this.frame);
  };

  private render() {
    const ctx = this.ctx;
    ctx.save();
    ctx.scale(this.dpr, this.dpr);

    this.drawBackground(ctx);

    ctx.save();
    this.camera.apply(ctx, this.viewW, this.viewH);
    this.drawWorldBounds(ctx);

    const { hw, hh } = this.camera.halfExtents(this.viewW, this.viewH);
    const cx = this.camera.x;
    const cy = this.camera.y;
    const pad = 90;
    const visible = (x: number, y: number, r: number) =>
      x + r > cx - hw - pad && x - r < cx + hw + pad && y + r > cy - hh - pad && y - r < cy + hh + pad;

    for (const poop of this.poops) {
      if (!visible(poop.x, poop.y, poop.radius * 2)) continue;
      ctx.save();
      ctx.globalAlpha = poop.alpha;
      ctx.translate(poop.x, poop.y);
      const s = 0.6 + 0.4 * poop.spawn;
      ctx.scale(s, s);
      const bob = Math.sin(this.player.animT * 3 + poop.bobPhase) * poop.radius * 0.08;
      drawPoop(ctx, poop.radius, this.player.animT, poop.golden, bob);
      ctx.restore();
    }

    for (const b of this.beings) {
      if (!visible(b.x, b.y, b.radius * 2)) continue;
      ctx.save();
      ctx.translate(b.x, b.y);
      drawBeing(ctx, b.kind, b.radius, b.animT, b.faceX, b.faceY, b.hunting);
      ctx.restore();
    }

    if (visible(this.buddha.x, this.buddha.y, this.buddha.radius * 3)) {
      ctx.save();
      ctx.translate(this.buddha.x, this.buddha.y);
      drawBuddha(ctx, this.buddha.radius, this.buddha.animT, this.buddha.faceX, this.buddha.faceY);
      ctx.restore();
    }

    ctx.save();
    ctx.translate(this.player.x, this.player.y);
    // overfull tints the body toward muddy brown
    drawCreature(
      ctx,
      stageAt(this.player.stage),
      this.player.radius,
      this.player.animT,
      this.player.faceX,
      this.player.faceY,
      this.player.squash,
      this.poisons.overfull,
    );
    ctx.restore();

    this.particles.draw(ctx);

    ctx.restore();
    ctx.restore();
  }

  private drawBackground(ctx: CanvasRenderingContext2D) {
    const g = ctx.createLinearGradient(0, 0, 0, this.viewH);
    g.addColorStop(0, "#0d1636");
    g.addColorStop(1, "#070c1f");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, this.viewW, this.viewH);
  }

  private drawWorldBounds(ctx: CanvasRenderingContext2D) {
    const W = CONFIG.world.width;
    const H = CONFIG.world.height;
    // lapis ground
    const g = ctx.createRadialGradient(W / 2, H / 2, 200, W / 2, H / 2, W * 0.75);
    g.addColorStop(0, "#1a2b5e");
    g.addColorStop(1, "#0b1533");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // faint mandala grid
    ctx.strokeStyle = "rgba(201,154,52,0.06)";
    ctx.lineWidth = 2;
    const grid = 200;
    ctx.beginPath();
    for (let x = 0; x <= W; x += grid) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
    }
    for (let y = 0; y <= H; y += grid) {
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
    }
    ctx.stroke();

    // gilded border
    ctx.strokeStyle = "rgba(201,154,52,0.5)";
    ctx.lineWidth = 10;
    ctx.strokeRect(0, 0, W, H);
  }
}
